import * as React from 'react';
// Import services.
import {
	DateService,
} from '@app/services';
// Import material ui modules.
import {
	Check,
	Close,
} from '@mui/icons-material';
// Import hooks.
import {
	useInputDate,
	UseInputDateResults,
	UseInputFormResult,
	useInputYesNo,
	UseInputYesNoResults
} from '@app/hooks/view/input';
// Import props.
import {
	FormPopupChangeEndorsementDateProps,
	FormPopupProps,
	NavigationFooterProps,
	ScrollbarProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
	PopupWidth,
} from '@app/enumerations';

interface UseFormPopupChangeEndorsementDateParameters {
	id: string;
	props: FormPopupChangeEndorsementDateProps,
	showValidations?: boolean;
}

interface UseFormPopupChangeEndorsementDateResults {
	submitForm: () => void;
	applyForNextTermInput: UseInputYesNoResults;
	endorsementDateInput: UseInputDateResults;
	applyFormPopupProps: () => FormPopupProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
	isEndorsementDateInputEnabled: () => boolean;
	isRenewalDateProvided: () => boolean;
}

const useFormPopupChangeEndorsementDate = (params: UseFormPopupChangeEndorsementDateParameters): UseFormPopupChangeEndorsementDateResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (): void => {
		// First flag all inputs to show any validation errors.
		flagAllInputsToShowValidations();
		// Validate the whole form.
		let isValid: boolean = checkFormValidation(true);
		// Make sure the confirmation popup appears if it's required.
		if (isValid) {
			let endorsementDate: Date | undefined = undefined;

			if (isRenewalDateProvided() && isAppliedForNextTerm()) {
				endorsementDate = params.props.renewalEndorsementDate;
			}
			else {
				endorsementDate = endorsementDateInput.value;
			}

			if (endorsementDate !== undefined) {
				params.props.onSaveEndorsementDate(endorsementDate);
			}
		}
	}

	/**
	 * Returns props for form popup. 
	 **/
	const applyFormPopupProps = (): FormPopupProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: params.props.popupTitle ?? 'Endorsement Date',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: true,
			isOpen: params.props.isOpen,
			popupWidth: PopupWidth.MEDIUM,
			onCloseClick: params.props.onClose,
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Close',
			leftButtonClick: params.props.onClose,
			leftButtonIcon: Close,
			rightButtonDisabled: false,
			rightButtonLabel: 'Save',
			rightButtonClick: submitForm,
			rightButtonIcon: Check,
			alignment: NavigationFooterAlignment.RIGHT,
		};
	};

	const isEndorsementDateInputEnabled = (): boolean => {
		return !isRenewalDateProvided()
			|| (applyForNextTermInput.value !== undefined && !isAppliedForNextTerm())
	}

	const isRenewalDateProvided = (): boolean => {
		return params.props.renewalEndorsementDate !== undefined;
	}

	// #endregion exported methods


	// #region private methods

	const isRenewalDateSelected = (date: Date | undefined): boolean | undefined => {
		let isSelected: boolean | undefined = undefined;
		if (date !== undefined && params.props.renewalEndorsementDate !== undefined) {
			isSelected = date.getDay() === params.props.renewalEndorsementDate.getDay()
				&& date.getMonth() === params.props.renewalEndorsementDate.getMonth()
				&& date.getFullYear() === params.props.renewalEndorsementDate.getFullYear()
		}

		return isSelected;
	}

	/**
	 * Validates an individual input.
	 * @param input
	 */
	const checkInputValidation = <T>(input: UseInputFormResult<T>, focusToElement: boolean): boolean => {
		let isValid = input.isInputValid;
		if (!isValid && focusToElement) {
			input.focusToInput();
		}
		return isValid;
	}

	/**
	 * Method to check form validation. 
	 **/
	const checkFormValidation = (focusToElement: boolean): boolean => {
		return checkInputValidation<boolean | undefined>(applyForNextTermInput, focusToElement)
			&& checkInputValidation<Date | undefined>(endorsementDateInput, focusToElement);
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		applyForNextTermInput.changeShowValidationFlag(true);
		endorsementDateInput.changeShowValidationFlag(true);
	}

	const getFormattedRenewalDate = (): string | undefined => {
		let formattedRenewalDate: string | undefined = undefined;
		if (params.props.renewalEndorsementDate !== undefined) {
			formattedRenewalDate = DateService.getInstance().formatDate(params.props.renewalEndorsementDate);
		}

		return formattedRenewalDate;
	}

	const getApplyForNextTermLabel = (): string => {
		const formattedRenewalDate: string | undefined = getFormattedRenewalDate();
		let label: string = String.empty();
		if (!String.isNullOrWhiteSpace(formattedRenewalDate)) {
			label = `Will this endorsement be applied towards the next term starting on ${formattedRenewalDate}?`;
		}
		else {
			label = 'Will this enodrsement be applied towards the next term?';
		}

		return label;
	}

	const getApplyForNextTermHelperTextLabel = (): string => {
		const formattedRenewalDate: string | undefined = getFormattedRenewalDate();
		let label: string = String.empty();
		if (!String.isNullOrWhiteSpace(formattedRenewalDate)) {
			label = `If yes, this endorsement will not take effect until ${formattedRenewalDate}.`;
		}
		else {
			label = 'If yes, this endorsement will not take effect until the first day of the next term.';
		}

		return label;
	}

	const isAppliedForNextTerm = (): boolean => {
		return applyForNextTermInput.value ?? false;
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Generate id for the apply for next term question. 
	 **/
	const generateIdForApplyForNextTerm = (): string => {
		return `${generateIdForForm()}-applyForNextTerm`;
	}

	/**
	 * Generate id for the endorsement date. 
	 **/
	const generateIdForEndorsementDate = (): string => {
		return `${generateIdForForm()}-endorsementDate`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	// Instantiate the 'apply for next term' input.
	const applyForNextTermInput: UseInputYesNoResults = useInputYesNo({
		id: generateIdForApplyForNextTerm(),
		label: getApplyForNextTermLabel(),
		infoLabel: getApplyForNextTermHelperTextLabel(),
		required: true,
		disabled: !isRenewalDateProvided(),
		isYesChecked: isRenewalDateSelected(params.props.endorsementDate),
		showValidation: params.showValidations,
	});

	// Instantiate the 'endorsement date' input hook.
	const endorsementDateInput: UseInputDateResults = useInputDate({
		id: generateIdForEndorsementDate(),
		label: 'Endorsement Date',
		required: true,
		disabled: !isEndorsementDateInputEnabled(),
		value: params.props.endorsementDate,
		minDate: params.props.minimumEndorsementDate,
		maxDate: params.props.maximumEndorsementDate,
		showValidation: params.showValidations,
	});

	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// If validations are to be shown, then initially scroll to the first invalidated input.
		if (!params.showValidations
			|| checkFormValidation(true)) {
			// If validations pass or are not to be shown, then initially the first input should be focused.
			if (isRenewalDateProvided()) {
				applyForNextTermInput.focusToInput();
			}
			else {
				endorsementDateInput.focusToInput();
			}
		}
	}, []);

	React.useEffect(() => {
		if (isAppliedForNextTerm()) {
			endorsementDateInput.setInput(undefined);
		}
	}, [applyForNextTermInput.value]);

	// #endregion life-cycle methods.

	return {
		submitForm: submitForm,
		applyForNextTermInput: applyForNextTermInput,
		endorsementDateInput: endorsementDateInput,
		applyFormPopupProps: applyFormPopupProps,
		applyNavigationFooterProps: applyNavigationFooterProps,
		isEndorsementDateInputEnabled: isEndorsementDateInputEnabled,
		isRenewalDateProvided: isRenewalDateProvided,
	};
}

// Export parameters.
export {
	UseFormPopupChangeEndorsementDateParameters,
}

// Export results.
export {
	UseFormPopupChangeEndorsementDateResults,
}

export default useFormPopupChangeEndorsementDate;